/**
 * isiXhosa: Compose
 *
 * What this file covers: Writing a single message to one person or a few, outside a conversation.
 *
 * Tone: Friendly and direct. Make clear who will receive the message and on which channel.
 *
 * isiXhosa: Everyday isiXhosa as used in South African business. Keys not translated yet fall back to English,
 * so a missing key is never an error.
 *
 * Rules for every translation: keep each key exactly as it is and translate only the text after it. Keep
 * {{placeholders}} such as {{count}} or {{name}} unchanged, and keep product names (RepletoryReach, WhatsApp,
 * Meta, Google, Outlook) as they are. Never use the em dash.
 */

import type { Catalog } from '../types';

const compose: Catalog = {
  'compose.title': 'Umyalezo omtsha',
  'compose.description': 'Thumela umyalezo kumntu omnye, okanye kwabambalwa. Bakhethe ngegama okanye ngenombolo, kwaye impendulo iya kufika kwibhokisi yakho.',
  'compose.step.who': 'Uya kubani',
  'compose.step.what': 'Uthini',
  'compose.step.send': 'Iindleko nokuthumela',
  'compose.number.label': 'Thumela usuka ku',
  'compose.number.hint': 'Inombolo lo myalezo ophuma kuyo. Impendulo ifika kwibhokisi yaloo nombolo.',
  'compose.recipients.label': 'Uya ku',
  'compose.recipients.hint': 'Khangela igama okanye inombolo, okanye chwetheza inombolo engekagcinwa.',
  'compose.recipients.placeholder': 'Igama okanye inombolo',
  'compose.recipients.remove': 'Susa {{who}}',
  'compose.recipients.notInBook': 'Akakho koonxibelelwano bakho okwangoku',
  'compose.recipients.noName': 'Akukho gamaligciniweyo',
  'compose.recipients.noMatches': 'Akukho mntu ufanayo noko. Chwetheza inombolo epheleleyo ukuze uthumele komtsha.',
  'compose.recipients.count': '{{count}} kwabangu-{{max}} abakhethiweyo',
  'compose.recipients.clear': 'Susa bonke',
  'compose.recipients.full.title': 'Yiloo nto kuphela eyamkelekayo apha',
  'compose.recipients.full.body': 'Umyalezo obhalwe apha uya kubantu abangadluli ku-{{max}}. Ukuba bangaphezulu koko, thumela usasazo kwithegi okanye kwicandelo.',
  'compose.window.badge.open': 'Angafumana umyalezo',
  'compose.window.badge.needsTemplate': 'Ufuna itemplethi',
  'compose.window.badge.templateOnly': 'Itemplethi kuphela',
  'compose.window.badge.optedOut': 'Uphumile',
  'compose.window.why.openHours': 'Ukubhalele, kusele iiyure ezingu-{{hours}} zokuphendula ngamazwi akho',
  'compose.window.why.openMinutes': 'Ukubhalele, kusele imizuzu engu-{{minutes}} yokuphendula ngamazwi akho',
  'compose.window.why.never': 'Akazange akubhalele',
  'compose.window.why.expiredDays': 'Ugqibele ukuva kuye kwiintsuku ezingu-{{days}} ezidlulileyo',
  'compose.window.why.expiredHours': 'Ugqibele ukuva kuye kwiiyure ezingu-{{hours}} ezidlulileyo',
  'compose.window.why.optedOut': 'Ucele ukungayifumani imiyalezo',
  'compose.window.closed.title': 'Lo ufuna itemplethi eyamkelweyo',
  'compose.window.closed.body': 'I-WhatsApp ivumela umyalezo ngamazwi akho kuphela kwiiyure ezingama-24 emva kokuba umntu ekubhalele. Khetha itemplethi eyamkelweyo endaweni yoko.',
  'compose.window.mixed.title': 'Aba bantu abakwindawo enye',
  'compose.window.mixed.body': 'Abangu-{{closed}} kwabangu-{{total}} abakubhalelanga kwiiyure ezingama-24 ezidlulileyo, kwaye umyalezo omnye uya kubo bonke, ngoko kufuneka ibe yitemplethi eyamkelweyo.',
  'compose.window.optedOut': 'Abangu-{{count}} kwabakhethiweyo bacele ukungayifumani imiyalezo. Bayashiywa kwaye abahlawulelwa.',
  'compose.message.label': 'Umyalezo',
  'compose.message.hint': 'Ngamazwi akho. Chwetheza u-{{ ukongeza into ngomntu ngamnye, njengegama lakhe.',
  'compose.message.placeholder': 'Bhala umyalezo wakho',
  'compose.message.count': '{{length}} kwangu-{{max}}',
  'compose.message.previewEmpty': 'Umyalezo wakho uya kubonakala apha',
  'compose.message.previewBusiness': 'Ishishini lakho',
  'compose.template.label': 'Itemplethi',
  'compose.template.hint': 'Ziitemplethi ezamkelwe yi-WhatsApp kuphela ezinokuthunyelwa ngaphandle kwefestile yeeyure ezingama-24.',
  'compose.template.placeholders': 'Zalisa izikhewu',
  'compose.template.placeholders.hint': 'Chwetheza u-{{ ukuzalisa isikhewu kwikhadi lomntu ngamnye, njengegama lakhe. Umntu ngamnye ufumana ixabiso lakhe.',
  'compose.template.placeholder': 'Isikhewu {{n}}',
  'compose.template.noPlaceholders': 'Le templethi ayinazo izikhewu ekufuneka zizalisiwe.',
  'compose.template.noneApproved.title': 'Azikho iitemplethi ezamkelweyo okwangoku',
  'compose.template.noneApproved.body': 'I-WhatsApp kufuneka yamkele itemplethi ngaphambi kokuba ithunyelwe kumntu ongakubhalelanga kutshanje.',
  'compose.template.noneApproved.action': 'Yenza itemplethi',
  'compose.template.devFallbackHidden': 'Iitemplethi ezingu-{{count}} zifihliwe kuba i-WhatsApp ingekazamkeli.',
  'compose.summary.recipients': 'Abantu',
  'compose.summary.excluded': 'Abangu-{{count}} bashiyiwe',
  'compose.summary.cost': 'Le nto iya kuxabisa',
  'compose.summary.balance': 'Ibhalansi',
  'compose.summary.balanceAfter': '{{after}} eseleyo emva kokuthumela',
  'compose.summary.balanceUnknown': 'Khetha ukuba uya kubani, siza kukubalela.',
  'compose.summary.short.title': 'Azanele iikhredithi',
  'compose.summary.short.body': 'Ufuna {{shortfall}} ngaphezulu ukuze uthumele oku.',
  'compose.summary.short.action': 'Faka imali',
  'compose.summary.broadcastHint': 'Uthumela kubantu abaninzi?',
  'compose.summary.broadcastAction': 'Thumela usasazo',
  'compose.action.send': 'Thumela kwabangu-{{count}}',
  'compose.action.sendTemplate': 'Thumela itemplethi kwabangu-{{count}}',
  'compose.error.bodyRequired': 'Bhala umyalezo phambi kokuba uwuthumele.',
  'compose.error.templateRequired': 'Khetha itemplethi eyamkelweyo.',
  'compose.error.placeholderRequired': 'Zalisa oku phambi kokuthumela.',
  'compose.error.numbersFailed': 'Iinombolo zakho ze-WhatsApp azikwazanga ukulayishwa okwangoku.',
  'compose.error.noNumbers.title': 'Akukho nombolo ye-WhatsApp equkiweyo',
  'compose.error.noNumbers.body': 'Umyalezo kufuneka uphume kwinombolo eqhagamshelweyo. Qhagamshela enye, ube unokuthumela nakubani na.',
  'compose.error.noNumbers.action': 'Qhagamshela inombolo',
  'compose.refusal.windowClosed': 'Abanye baba bantu abakubhalelanga kwiiyure ezingama-24 ezidlulileyo, ngoko kufuneka ibe yitemplethi eyamkelweyo. Akukho nto ithunyelweyo.',
  'compose.refusal.TOO_MANY_RECIPIENTS': 'Ngabantu abaninzi ngaphezu koko kwamkelwa ngumyalezo obhalwe apha. Thumela usasazo kwithegi okanye kwicandelo endaweni yoko.',
  'compose.refusal.NUMBER_REQUIRED': 'Yithi lo myalezo uphuma kweyiphi inombolo yakho.',
  'compose.refusal.NO_ACTIVE_NUMBER': 'Akukho nombolo ye-WhatsApp eqhagamshelweyo, ngoko akukho nto inokuthunyelwa.',
  'compose.refusal.VALIDATION': 'Kukho into engekazaliswa kulo myalezo.',
  'compose.refusal.unknown': 'Oku akukwazanga ukuthunyelwa okwangoku. Zama kwakhona emzuzwini.',
  'compose.toast.sent': 'Kuthunyelwe kwabangu-{{count}}.',
  'compose.toast.partial': 'Kuthunyelwe kwabangu-{{sent}}, kodwa abangu-{{failed}} abafikelelwanga.',
  'compose.toast.noneSent': 'Akukho nto ithunyelweyo. Jonga ngezantsi ukuba kwenzeke ntoni.',
  'compose.results.allSent.title': 'Konke kuthunyelwe',
  'compose.results.allSent.body': 'Oku kufikelele bonke abangu-{{count}}. Iimpendulo zabo ziya kufika kwibhokisi yakho.',
  'compose.results.noneSent.title': 'Akukho nto ithunyelweyo',
  'compose.results.noneSent.body': 'Akukho mntu kwabangu-{{count}} ofikelelweyo. Into engahambanga kakuhle iboniswa ecaleni komntu ngamnye.',
  'compose.results.partial.title': 'Eminye yale miyalezo ayifikanga',
  'compose.results.partial.body': 'Abangu-{{sent}} bafikelelwe kwaye abangu-{{failed}} abafikelelwanga. Ungazama kwakhona abo bangaphumelelanga ngaphandle kokuthumela kabini komnye.',
  'compose.results.delivered': 'Isendleleni',
  'compose.results.open': 'Vula incoko',
  'compose.results.badge.sent': 'Kuthunyelwe',
  'compose.results.badge.failed': 'Akuthunyelwanga',
  'compose.results.backToInbox': 'Buyela kwibhokisi',
  'compose.results.another': 'Bhala omnye',
  'compose.results.retry': 'Zama abangu-{{count}} kwakhona',
  'compose.results.reason.unknown': 'Lo akafikanga.',
  'compose.results.reason.OPTED_OUT': 'Bacele ukungayifumani imiyalezo.',
  'compose.results.reason.WINDOW_CLOSED': 'Abakubhalelanga kwiiyure ezingama-24 ezidlulileyo, ngoko oku bekufuna itemplethi eyamkelweyo.',
  'compose.results.reason.INSUFFICIENT_CREDITS': 'Bezinganelanga iikhredithi zokuthumela lo.',
  'compose.results.reason.PHONE_NOT_FOUND': 'Inombolo oku okuthunyelwe kuyo ayisaqhagamshelwanga.',
  'compose.results.reason.CHANNEL_UNREACHABLE': 'I-WhatsApp ayifikelelekanga. Akukho nto ithunyelwe kubo.',
  'compose.results.reason.SEND_FAILED': 'I-WhatsApp ayimamkelanga lo.',
  'compose.results.reason.META_REFUSED': 'I-WhatsApp ayimamkelanga lo.',
  'compose.results.reason.SEND_UNREADABLE': 'Oku kuphumile kodwa asikwazanga ukuqinisekisa okwenzekileyo.',
  'compose.number.unnamed': 'Inombolo esasasetyenzwa',
  'compose.results.reason.ACCOUNT_NOT_PAYABLE': 'I-WhatsApp iyalile ngenxa yengxaki yentlawulo kwicala lethu, hayi elakho. Siyayilungisa, kwaye akufuneki wenze nto kwa-Meta.',
  'compose.results.reason.UNDELIVERABLE': 'I-WhatsApp ayikwazanga ukuyisa kula nombolo.',
  'compose.results.reason.NUMBER_NOT_REGISTERED': 'Inombolo othumele ngayo ayibhaliswanga kwi-WhatsApp.',
  'compose.results.reason.MEDIA_NOT_FETCHED': 'I-WhatsApp ayikwazanga ukulanda ifayile eqhotyoshelweyo.'
};

export default compose;
